/**
 * useGuaranteeSimulation - Hook for running and tracking a guarantee simulation.
 *
 * Calls the guarantee service, polls for status every 5 seconds while running,
 * and exposes progress + results to the component.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  startGuaranteeSimulation,
  getGuaranteeRunStatus,
} from '../services/guaranteeService';

const POLL_INTERVAL_MS = 5000;

export function useGuaranteeSimulation(projectId) {
  const [runId, setRunId] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(null); // { day, total_days, pct }
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Poll when runId changes and we're running
  useEffect(() => {
    if (!runId || !isRunning || !projectId) return;

    async function poll() {
      try {
        const data = await getGuaranteeRunStatus(projectId, runId);

        if (data.progress) {
          setProgress(data.progress);
        }

        if (data.status === 'completed' || data.status === 'complete') {
          setIsRunning(false);
          setResults(data.results);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        } else if (data.status === 'failed' || data.status === 'error') {
          setIsRunning(false);
          setError(data.error || 'Simulation failed');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err) {
        setIsRunning(false);
        setError(err.message);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }

    // Initial poll immediately
    poll();

    // Then every POLL_INTERVAL_MS
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runId, isRunning, projectId]);

  const startSimulation = useCallback(async (scenarioId) => {
    if (!projectId) {
      setError('Missing projectId');
      return;
    }
    if (!scenarioId) {
      setError('Missing scenarioId');
      return;
    }

    setError(null);
    setResults(null);
    setProgress(null);
    setIsRunning(true);

    try {
      const data = await startGuaranteeSimulation(projectId, scenarioId);
      setRunId(data.run_id);
    } catch (err) {
      setIsRunning(false);
      setError(err.message);
    }
  }, [projectId]);

  const reset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunId(null);
    setIsRunning(false);
    setProgress(null);
    setResults(null);
    setError(null);
  }, []);

  return {
    isRunning,
    progress,
    results,
    error,
    startSimulation,
    reset,
  };
}
